import React, {useEffect, useState} from 'react';
import {Flex, Form, Input, Modal} from "antd";
import CustomButton from "../CustomButton/CustomButton";
import { ReactComponent as CrossIcon } from '../../../assets/images/global-icons/cross_ic.svg';
import { ReactComponent as PassIcon } from '../../../assets/images/global-icons/lock_ic.svg'
import { ReactComponent as OpenEyeIcon } from '../../../assets/images/global-icons/eye_ic.svg'
import { ReactComponent as CloseEyeIcon } from '../../../assets/images/global-icons/eye_crossed_ic.svg'
import './ChangePasswordModal.scss'
import {useForm} from "antd/lib/form/Form";
import styles from "../../../pages/Login/Login.module.scss";
import { useChangePasswordMutation } from '../../../redux/API/userSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useGetMeQuery } from '../../../redux/API/userSlice';
import Toast from "../../LeftMenu/components/Toast/Toast";
interface ChangePasswordModalProps {
  visible: boolean;
  onClose: () => void;
  onForgotPasswordClick: () => void; 
}


const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ visible, onClose, onForgotPasswordClick }) => {
  const [form] = useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [showPasswordChangedToast, setShowPasswordChangedToast] = useState(false);
  const [changePassword] = useChangePasswordMutation();
  const { isAuth } = useSelector((state: RootState) => state.isAuth); 
  const [userEmail, setUserEmail] = useState<string>('');
  const { data: user } = useGetMeQuery({ accessToken: isAuth });

  useEffect(() => {
    setUserEmail(user?.data?.email || '');
  }, [user]);

  const handleFinish = async () => {
    try {
      const { currentPassword, newPassword } = form.getFieldsValue();

      await changePassword({
        email: userEmail,
        oldPassword: currentPassword,
        newPassword
      }).unwrap();

      setShowPasswordChangedToast(true);
      onClose(); 
    } catch (error) {
      console.error('Failed to change password:', error);

      form.setFields([
        {
          name: 'currentPassword',
          errors: ['Old password for this email is incorrect']
        }
      ]);
    }
  };


  useEffect(() => {
    form.resetFields();
  }, [visible]);

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
    const allTouched = form.isFieldsTouched(true);

    setIsButtonDisabled(hasErrors || !allTouched);
  };

  return (
    <>
      {showPasswordChangedToast && (
        <Toast onClose={() => setShowPasswordChangedToast(false)} content="Password has been successfully changed!" />
      )}
      <Modal
        open={visible}
        footer={null}
        closable={false}
        maskClosable={true}
        onCancel={onClose}
        centered
        className={'ChangePasswordModal'}
      >
        <Flex vertical>
          <Flex justify="space-between" align="center" className={'ChangePasswordModal__header'}>
            <h3>Change password</h3>
            <CrossIcon color={'#000000'} onClick={onClose} />
          </Flex>
          <Flex className={'ChangePasswordModal__body'}>
            <Form
              form={form}
              onFinish={handleFinish}
              layout="vertical"
              className={'ChangePasswordModal__body__form'}
              onFieldsChange={handleFormChange}
            >
              <Form.Item
                label="Your current password"
                name="currentPassword"
                rules={[
                  {
                    required: true,
                    min: 6,
                    message: 'Current password must be at least 6 characters'
                  }
                ]}
              >
                <Input.Password
                  prefix={<PassIcon className="icon-lock" />}
                  placeholder="Password"
                  className="ChangePasswordModal__body__form__input"
                  iconRender={(visible) =>
                    visible ? <OpenEyeIcon className="icon-eye-open" /> : <CloseEyeIcon className="icon-eye-closed" />
                  }
                />
              </Form.Item>
              <Flex className="ChangePasswordModal__body__form__forgotPassword" justify="flex-end">
              <span className="ChangePasswordModal__forgot" onClick={onForgotPasswordClick}>
                Forgot password?
              </span>
              </Flex>

              <Flex vertical gap={'20px'}>
                <Form.Item
                  label="Enter a new password"
                  name="newPassword"
                  dependencies={['currentPassword']}
                  rules={[
                    {
                      required: true,
                      min: 6,
                      message: 'New password must be at least 6 characters'
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('currentPassword') !== value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('New password must be different from current password'));
                      }
                    })
                  ]}
                >
                  <Input.Password
                    prefix={<PassIcon className="icon-lock" />}
                    placeholder="Password"
                    className={styles.Login__form__input}
                    iconRender={(visible) =>
                      visible ? <OpenEyeIcon className="icon-eye-open" /> : <CloseEyeIcon className="icon-eye-closed" />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm the new password"
                  name="confirmPassword"
                  dependencies={['newPassword']}
                  rules={[
                    {
                      required: true,
                      min: 6,
                      message: ''
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      }
                    })
                  ]}
                >
                  <Input.Password
                    prefix={<PassIcon className="icon-lock" />}
                    placeholder="Password"
                    className={styles.Login__form__input}
                    iconRender={(visible) =>
                      visible ? <OpenEyeIcon className="icon-eye-open" /> : <CloseEyeIcon className="icon-eye-closed" />
                    }
                  />
                </Form.Item>
              </Flex>

              <Form.Item>
                <Flex className={'ChangePasswordModal__body__form__button'}>
                  <CustomButton text="Save" disable={isButtonDisabled} htmlType="submit" />
                </Flex>
              </Form.Item>
            </Form>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;